import {
  WEEK_ORDER,
  clashOffers,
  findPlacesProblems,
  findTimetableProblems,
  minutesOf,
  problemDetail,
  publishFromDraft,
  type ClashOffer,
  type DraftGroup,
  type DraftItem,
  type FoundProblem,
  type ProblemSlot,
  type TimetableDraft,
  type TimetableEditor,
} from "@teamna/shared";
import { SNAP_MIN, layoutDay, shortTimeLabel, snap, timeLabel, type Placed } from "../calendar/layout.ts";

/**
 * The week editor's own helpers, on top of the shared draft model (`@teamna/shared` timetable.ts).
 * Pure, so the tests can drive them without a browser.
 */

export { SNAP_MIN, layoutDay, shortTimeLabel, snap, timeLabel, type Placed };

export const DEFAULT_DAY = { start: 8 * 60, end: 20 * 60 };

/** The people booked on each row: on its next session, on its fullest one, and per date. */
export type BookedIndex = {
  next: Map<string, number>;
  most: Map<string, number>;
  /** All future bookings of a row. */
  total: Map<string, number>;
  onDate: Map<string, number>;
};

export function bookedIndex(editor: Pick<TimetableEditor, "sessions">): BookedIndex {
  const next = new Map<string, number>();
  const most = new Map<string, number>();
  const total = new Map<string, number>();
  const onDate = new Map<string, number>();
  for (const session of editor.sessions) {
    if (!next.has(session.templateId)) next.set(session.templateId, session.bookedCount);
    most.set(session.templateId, Math.max(most.get(session.templateId) ?? 0, session.bookedCount));
    total.set(session.templateId, (total.get(session.templateId) ?? 0) + session.bookedCount);
    onDate.set(`${session.templateId}|${session.date}`, session.bookedCount);
  }
  return { next, most, total, onDate };
}

/** The rows of an item on one weekday: one for a class, one per private session for free hours. */
export function rowIdsOn(item: DraftItem, weekday: number): string[] {
  if (!item.free) {
    const id = item.ids[String(weekday)];
    return id ? [id] : [];
  }
  return Object.entries(item.ids)
    .filter(([key]) => key.startsWith(`${weekday}@`))
    .map(([, id]) => id);
}

/** People booked on the item's next session that weekday (summed over private sessions). */
export function bookedNextOn(item: DraftItem, weekday: number, index: BookedIndex): number {
  return rowIdsOn(item, weekday).reduce((total, id) => total + (index.next.get(id) ?? 0), 0);
}

/** Every future booking of an item, on all its days. */
export function bookedTotal(item: DraftItem, index: BookedIndex, days: number[] = item.days): number {
  return days.reduce((sum, day) => sum + rowIdsOn(item, day).reduce((inner, id) => inner + (index.total.get(id) ?? 0), 0), 0);
}

export function bookedOnDate(item: DraftItem, date: string, weekday: number, index: BookedIndex): number {
  return rowIdsOn(item, weekday).reduce((total, id) => total + (index.onDate.get(`${id}|${date}`) ?? 0), 0);
}

/** The weekdays a column shows, in the business week (Saturday first). */
/**
 * The change in class sessions as words, never a signed number: "44 fewer sessions", "1 more
 * session". A "−44" over the word "sessions" read as a count below zero.
 */
export function sessionsChange(delta: number): { value: number; label: string } {
  const value = Math.abs(delta);
  const noun = value === 1 ? "session" : "sessions";
  if (delta < 0) return { value, label: `fewer ${noun}` };
  if (delta > 0) return { value, label: `more ${noun}` };
  return { value, label: "sessions added or removed" };
}

export function weekColumns(): number[] {
  return [...WEEK_ORDER];
}

/** The usual hours of the week: 08:00 to 20:00 at least, wider when a block is outside. The board opens on them (it always has the whole day); the fix offers stay in them. */
export function dayRange(items: DraftItem[]): { start: number; end: number } {
  const start = Math.min(DEFAULT_DAY.start, ...items.map((item) => Math.floor(item.start / 60) * 60));
  const end = Math.max(DEFAULT_DAY.end, ...items.map((item) => Math.ceil(item.end / 60) * 60));
  return { start: Math.max(0, start), end: Math.min(24 * 60, end) };
}


/**
 * The coaches of a level in the editor: its own list first, then anyone who already has one of its
 * weekly times in the draft. A level with one coach on its list can still share its times.
 */
export function levelCoaches(group: { id: string; staffUserIds: readonly string[] }, draft: Pick<TimetableDraft, "items">): string[] {
  const used = draft.items.flatMap((item) => (item.groupId === group.id && !item.free && item.staffUserId ? [item.staffUserId] : []));
  return [...new Set([...group.staffUserIds, ...used])];
}

/** The team with a level's coaches first, in their order, then the rest as they were. */
export function coachesFirst<T extends { userId: string }>(team: readonly T[], coaches: readonly string[]): T[] {
  const rank = (userId: string) => {
    const index = coaches.indexOf(userId);
    return index === -1 ? coaches.length : index;
  };
  return [...team].sort((a, b) => rank(a.userId) - rank(b.userId));
}


// ---------------------------------------------------------------------------
// Problems, as the editor shows them
// ---------------------------------------------------------------------------

export type EditorProblem = FoundProblem & {
  booked?: number;
  detail: string;
  /** The items the problem is about, in the order of their first row. */
  items: string[];
  /** The item a fix applies to: the one the owner touched last, else the newest. */
  fix: string;
};

export function editorProblems(input: {
  draft: TimetableDraft;
  groups: DraftGroup[];
  daysOff: readonly number[];
  booked: BookedIndex;
  words: { groupName: (id: string) => string; personName: (id: string) => string; staff: string; people: string };
  touched: string | null;
}): EditorProblem[] {
  const request = publishFromDraft(input.draft, input.groups);
  const privateIds = new Set(input.groups.filter((group) => group.kind === "private").map((group) => group.id));
  const slots: ProblemSlot[] = request.slots.map((slot) => ({ ...slot, private: privateIds.has(slot.groupId) }));
  const found = [
    ...findTimetableProblems(slots, input.daysOff),
    ...findPlacesProblems(request.slots, (rowId) => input.booked.most.get(rowId) ?? 0),
  ];
  const order = new Map(input.draft.items.map((item, index) => [item.key, index]));
  return found.map((problem) => {
    const items = [...new Set(problem.slots.map((index) => request.owners[index]!.item))];
    const fix =
      (input.touched && items.includes(input.touched) ? input.touched : null) ??
      [...items].sort((a, b) => (order.get(b) ?? 0) - (order.get(a) ?? 0))[0]!;
    return { ...problem, detail: problemDetail(problem, slots, input.words), items, fix };
  });
}

export type Offer = { label: string; apply: (item: DraftItem) => DraftItem };

/** The one-click fixes the note offers for a problem. */
export function offersFor(
  problem: EditorProblem,
  draft: TimetableDraft,
  team: Array<{ userId: string; name: string }>,
  bounds: { start: number; end: number },
  /** The coaches of each level: a clash offers them before the rest of the team. */
  coachesOf: (groupId: string) => readonly string[] = () => [],
): Offer[] {
  const item = draft.items.find((row) => row.key === problem.fix);
  if (!item) return [];
  team = coachesFirst(team, coachesOf(item.groupId));
  const nameOf = (userId: string) => team.find((member) => member.userId === userId)?.name ?? "Team member";

  if (problem.code === "PLACES_BELOW_BOOKED" && problem.booked) {
    const booked = problem.booked;
    return [{ label: `Use ${booked}`, apply: (row) => ({ ...row, capacity: booked }) }];
  }
  if (problem.code === "PRIVATE_NEEDS_COACH") {
    return team
      .filter((member) => !draft.items.some((other) => other.key !== item.key && other.staffUserId === member.userId && overlaps(other, item)))
      .slice(0, 3)
      .map((member) => ({ label: member.name, apply: (row) => ({ ...row, staffUserId: member.userId }) }));
  }
  if (problem.code === "COACH_CLASH") {
    return clashOffers(draft, item.key, team.map((member) => member.userId), bounds).map((offer: ClashOffer) =>
      offer.kind === "person"
        ? { label: `Use ${nameOf(offer.userId)}`, apply: (row: DraftItem) => ({ ...row, staffUserId: offer.userId }) }
        : {
            label: `Use ${timeLabel(offer.start)}${item.free ? ` to ${timeLabel(offer.start + item.end - item.start)}` : ""}`,
            apply: (row: DraftItem) => ({ ...row, start: offer.start, end: offer.start + (row.end - row.start) }),
          },
    );
  }
  return [];
}

function overlaps(a: DraftItem, b: DraftItem): boolean {
  return a.days.some((day) => b.days.includes(day)) && a.start < b.end && b.start < a.end;
}


export { minutesOf };

/** Where the owner dragged the time card to, in pixels from the top left of the board. */
export type CardSpot = { left: number; top: number };

/** The space between the time card and the edge of the board, in pixels. */
export const CARD_EDGE = 12;

/**
 * The place of the time card nearest to `spot` where the card is fully on the board, `CARD_EDGE` from
 * each edge. A board smaller than the card keeps the card at the top left.
 */
export function fitCard(spot: CardSpot, board: { width: number; height: number }, card: { width: number; height: number }): CardSpot {
  const maxLeft = Math.max(CARD_EDGE, board.width - card.width - CARD_EDGE);
  const maxTop = Math.max(CARD_EDGE, board.height - card.height - CARD_EDGE);
  return { left: Math.min(Math.max(spot.left, CARD_EDGE), maxLeft), top: Math.min(Math.max(spot.top, CARD_EDGE), maxTop) };
}
